/** Animal types and dog breeds for Property Data pet policy. Ids match backend pet_policy.py. */

export type PetOption = {
    id: string
    label: string
}

export type DogBreedMode = 'all' | 'allowlist'

export type PetsPolicyDraft = {
    allowed_animal_types: string[]
    dog_breeds: {
        mode: DogBreedMode
        ids: string[]
    }
    max_pets_per_unit: string
    counts_toward_cap: Record<string, boolean>
    deposit: string
    fee: string
    rent: string
}

export const PET_FEE_FIELDS = [
    { id: 'deposit', label: 'Pet deposit' },
    { id: 'fee', label: 'One-time pet fee' },
    { id: 'rent', label: 'Monthly pet rent' },
] as const

export type PetFeeKey = typeof PET_FEE_FIELDS[number]['id']

export const ANIMAL_TYPES: PetOption[] = [
    { id: 'dog', label: 'Dog' },
    { id: 'cat', label: 'Cat' },
    { id: 'fish', label: 'Fish' },
    { id: 'reptile', label: 'Reptile' },
    { id: 'rodent', label: 'Rodent' },
    { id: 'bird', label: 'Bird' },
]

export const DEFAULT_COUNTS_TOWARD_CAP: Record<string, boolean> = {
    dog: true,
    cat: true,
    fish: false,
    reptile: true,
    rodent: false,
    bird: false,
}

export const DOG_ANIMAL_TYPE_ID = 'dog'

export const DOG_BREEDS: PetOption[] = [
    { id: 'mixed_unknown', label: 'Mixed / unknown' },
    { id: 'affenpinscher', label: 'Affenpinscher' },
    { id: 'afghan_hound', label: 'Afghan Hound' },
    { id: 'airedale_terrier', label: 'Airedale Terrier' },
    { id: 'akita', label: 'Akita' },
    { id: 'alaskan_malamute', label: 'Alaskan Malamute' },
    { id: 'american_english_coonhound', label: 'American English Coonhound' },
    { id: 'american_eskimo_dog', label: 'American Eskimo Dog' },
    { id: 'american_foxhound', label: 'American Foxhound' },
    { id: 'american_hairless_terrier', label: 'American Hairless Terrier' },
    {
        id: 'american_staffordshire_terrier',
        label: 'American Staffordshire Terrier',
    },
    { id: 'american_water_spaniel', label: 'American Water Spaniel' },
    { id: 'anatolian_shepherd_dog', label: 'Anatolian Shepherd Dog' },
    { id: 'australian_cattle_dog', label: 'Australian Cattle Dog' },
    { id: 'australian_shepherd', label: 'Australian Shepherd' },
    { id: 'australian_terrier', label: 'Australian Terrier' },
    { id: 'azawakh', label: 'Azawakh' },
    { id: 'barbet', label: 'Barbet' },
    { id: 'basenji', label: 'Basenji' },
    { id: 'basset_hound', label: 'Basset Hound' },
    { id: 'beagle', label: 'Beagle' },
    { id: 'bearded_collie', label: 'Bearded Collie' },
    { id: 'beauceron', label: 'Beauceron' },
    { id: 'bedlington_terrier', label: 'Bedlington Terrier' },
    { id: 'belgian_laekenois', label: 'Belgian Laekenois' },
    { id: 'belgian_malinois', label: 'Belgian Malinois' },
    { id: 'belgian_sheepdog', label: 'Belgian Sheepdog' },
    { id: 'belgian_tervuren', label: 'Belgian Tervuren' },
    { id: 'bergamasco_sheepdog', label: 'Bergamasco Sheepdog' },
    { id: 'berger_picard', label: 'Berger Picard' },
    { id: 'bernese_mountain_dog', label: 'Bernese Mountain Dog' },
    { id: 'bichon_frise', label: 'Bichon Frise' },
    { id: 'biewer_terrier', label: 'Biewer Terrier' },
    { id: 'black_and_tan_coonhound', label: 'Black and Tan Coonhound' },
    { id: 'black_russian_terrier', label: 'Black Russian Terrier' },
    { id: 'bloodhound', label: 'Bloodhound' },
    { id: 'bluetick_coonhound', label: 'Bluetick Coonhound' },
    { id: 'boerboel', label: 'Boerboel' },
    { id: 'border_collie', label: 'Border Collie' },
    { id: 'border_terrier', label: 'Border Terrier' },
    { id: 'borzoi', label: 'Borzoi' },
    { id: 'boston_terrier', label: 'Boston Terrier' },
    { id: 'bouvier_des_flandres', label: 'Bouvier des Flandres' },
    { id: 'boxer', label: 'Boxer' },
    { id: 'boykin_spaniel', label: 'Boykin Spaniel' },
    { id: 'bracco_italiano', label: 'Bracco Italiano' },
    { id: 'briard', label: 'Briard' },
    { id: 'brittany', label: 'Brittany' },
    { id: 'brussels_griffon', label: 'Brussels Griffon' },
    { id: 'bull_terrier', label: 'Bull Terrier' },
    { id: 'bulldog', label: 'Bulldog' },
    { id: 'bullmastiff', label: 'Bullmastiff' },
    { id: 'cairn_terrier', label: 'Cairn Terrier' },
    { id: 'canaan_dog', label: 'Canaan Dog' },
    { id: 'cane_corso', label: 'Cane Corso' },
    { id: 'cardigan_welsh_corgi', label: 'Cardigan Welsh Corgi' },
    {
        id: 'cavalier_king_charles_spaniel',
        label: 'Cavalier King Charles Spaniel',
    },
    { id: 'cesky_terrier', label: 'Cesky Terrier' },
    { id: 'chesapeake_bay_retriever', label: 'Chesapeake Bay Retriever' },
    { id: 'chihuahua', label: 'Chihuahua' },
    { id: 'chinese_crested', label: 'Chinese Crested' },
    { id: 'chinese_shar_pei', label: 'Chinese Shar-Pei' },
    { id: 'chinook', label: 'Chinook' },
    { id: 'chow_chow', label: 'Chow Chow' },
    { id: 'cirneco_dell_etna', label: "Cirneco dell'Etna" },
    { id: 'clumber_spaniel', label: 'Clumber Spaniel' },
    { id: 'cocker_spaniel', label: 'Cocker Spaniel' },
    { id: 'collie', label: 'Collie' },
    { id: 'coton_de_tulear', label: 'Coton de Tulear' },
    { id: 'curly_coated_retriever', label: 'Curly-Coated Retriever' },
    { id: 'dachshund', label: 'Dachshund' },
    { id: 'dalmatian', label: 'Dalmatian' },
    { id: 'dandie_dinmont_terrier', label: 'Dandie Dinmont Terrier' },
    { id: 'danish_swedish_farmdog', label: 'Danish-Swedish Farmdog' },
    { id: 'doberman_pinscher', label: 'Doberman Pinscher' },
    { id: 'dogo_argentino', label: 'Dogo Argentino' },
    { id: 'dogue_de_bordeaux', label: 'Dogue de Bordeaux' },
    { id: 'english_cocker_spaniel', label: 'English Cocker Spaniel' },
    { id: 'english_foxhound', label: 'English Foxhound' },
    { id: 'english_setter', label: 'English Setter' },
    { id: 'english_springer_spaniel', label: 'English Springer Spaniel' },
    { id: 'english_toy_spaniel', label: 'English Toy Spaniel' },
    { id: 'entlebucher_mountain_dog', label: 'Entlebucher Mountain Dog' },
    { id: 'field_spaniel', label: 'Field Spaniel' },
    { id: 'finnish_lapphund', label: 'Finnish Lapphund' },
    { id: 'finnish_spitz', label: 'Finnish Spitz' },
    { id: 'flat_coated_retriever', label: 'Flat-Coated Retriever' },
    { id: 'french_bulldog', label: 'French Bulldog' },
    { id: 'german_pinscher', label: 'German Pinscher' },
    { id: 'german_shepherd_dog', label: 'German Shepherd Dog' },
    { id: 'german_shorthaired_pointer', label: 'German Shorthaired Pointer' },
    { id: 'german_wirehaired_pointer', label: 'German Wirehaired Pointer' },
    { id: 'giant_schnauzer', label: 'Giant Schnauzer' },
    { id: 'glen_of_imaal_terrier', label: 'Glen of Imaal Terrier' },
    { id: 'golden_retriever', label: 'Golden Retriever' },
    { id: 'gordon_setter', label: 'Gordon Setter' },
    {
        id: 'grand_basset_griffon_vendeen',
        label: 'Grand Basset Griffon Vendeen',
    },
    { id: 'great_dane', label: 'Great Dane' },
    { id: 'great_pyrenees', label: 'Great Pyrenees' },
    { id: 'greater_swiss_mountain_dog', label: 'Greater Swiss Mountain Dog' },
    { id: 'greyhound', label: 'Greyhound' },
    { id: 'harrier', label: 'Harrier' },
    { id: 'havanese', label: 'Havanese' },
    { id: 'ibizan_hound', label: 'Ibizan Hound' },
    { id: 'icelandic_sheepdog', label: 'Icelandic Sheepdog' },
    { id: 'irish_red_and_white_setter', label: 'Irish Red and White Setter' },
    { id: 'irish_setter', label: 'Irish Setter' },
    { id: 'irish_terrier', label: 'Irish Terrier' },
    { id: 'irish_water_spaniel', label: 'Irish Water Spaniel' },
    { id: 'irish_wolfhound', label: 'Irish Wolfhound' },
    { id: 'italian_greyhound', label: 'Italian Greyhound' },
    { id: 'japanese_chin', label: 'Japanese Chin' },
    { id: 'keeshond', label: 'Keeshond' },
    { id: 'kerry_blue_terrier', label: 'Kerry Blue Terrier' },
    { id: 'komondor', label: 'Komondor' },
    { id: 'kuvasz', label: 'Kuvasz' },
    { id: 'labrador_retriever', label: 'Labrador Retriever' },
    { id: 'lagotto_romagnolo', label: 'Lagotto Romagnolo' },
    { id: 'lakeland_terrier', label: 'Lakeland Terrier' },
    { id: 'lancashire_heeler', label: 'Lancashire Heeler' },
    { id: 'leonberger', label: 'Leonberger' },
    { id: 'lhasa_apso', label: 'Lhasa Apso' },
    { id: 'lowchen', label: 'Lowchen' },
    { id: 'maltese', label: 'Maltese' },
    { id: 'manchester_terrier', label: 'Manchester Terrier' },
    { id: 'mastiff', label: 'Mastiff' },
    { id: 'miniature_american_shepherd', label: 'Miniature American Shepherd' },
    { id: 'miniature_bull_terrier', label: 'Miniature Bull Terrier' },
    { id: 'miniature_pinscher', label: 'Miniature Pinscher' },
    { id: 'miniature_schnauzer', label: 'Miniature Schnauzer' },
    { id: 'mudi', label: 'Mudi' },
    { id: 'neapolitan_mastiff', label: 'Neapolitan Mastiff' },
    { id: 'nederlandse_kooikerhondje', label: 'Nederlandse Kooikerhondje' },
    { id: 'newfoundland', label: 'Newfoundland' },
    { id: 'norfolk_terrier', label: 'Norfolk Terrier' },
    { id: 'norwegian_buhund', label: 'Norwegian Buhund' },
    { id: 'norwegian_elkhound', label: 'Norwegian Elkhound' },
    { id: 'norwegian_lundehund', label: 'Norwegian Lundehund' },
    { id: 'norwich_terrier', label: 'Norwich Terrier' },
    {
        id: 'nova_scotia_duck_tolling_retriever',
        label: 'Nova Scotia Duck Tolling Retriever',
    },
    { id: 'old_english_sheepdog', label: 'Old English Sheepdog' },
    { id: 'otterhound', label: 'Otterhound' },
    { id: 'papillon', label: 'Papillon' },
    { id: 'parson_russell_terrier', label: 'Parson Russell Terrier' },
    { id: 'pekingese', label: 'Pekingese' },
    { id: 'pembroke_welsh_corgi', label: 'Pembroke Welsh Corgi' },
    {
        id: 'petit_basset_griffon_vendeen',
        label: 'Petit Basset Griffon Vendeen',
    },
    { id: 'pharaoh_hound', label: 'Pharaoh Hound' },
    { id: 'plott_hound', label: 'Plott Hound' },
    { id: 'pointer', label: 'Pointer' },
    { id: 'polish_lowland_sheepdog', label: 'Polish Lowland Sheepdog' },
    { id: 'pomeranian', label: 'Pomeranian' },
    { id: 'poodle', label: 'Poodle' },
    { id: 'portuguese_podengo_pequeno', label: 'Portuguese Podengo Pequeno' },
    { id: 'portuguese_water_dog', label: 'Portuguese Water Dog' },
    { id: 'pug', label: 'Pug' },
    { id: 'puli', label: 'Puli' },
    { id: 'pumi', label: 'Pumi' },
    { id: 'pyrenean_shepherd', label: 'Pyrenean Shepherd' },
    { id: 'rat_terrier', label: 'Rat Terrier' },
    { id: 'redbone_coonhound', label: 'Redbone Coonhound' },
    { id: 'rhodesian_ridgeback', label: 'Rhodesian Ridgeback' },
    { id: 'rottweiler', label: 'Rottweiler' },
    { id: 'russell_terrier', label: 'Russell Terrier' },
    { id: 'russian_toy', label: 'Russian Toy' },
    { id: 'saint_bernard', label: 'Saint Bernard' },
    { id: 'saluki', label: 'Saluki' },
    { id: 'samoyed', label: 'Samoyed' },
    { id: 'schipperke', label: 'Schipperke' },
    { id: 'scottish_deerhound', label: 'Scottish Deerhound' },
    { id: 'scottish_terrier', label: 'Scottish Terrier' },
    { id: 'sealyham_terrier', label: 'Sealyham Terrier' },
    { id: 'shetland_sheepdog', label: 'Shetland Sheepdog' },
    { id: 'shiba_inu', label: 'Shiba Inu' },
    { id: 'shih_tzu', label: 'Shih Tzu' },
    { id: 'siberian_husky', label: 'Siberian Husky' },
    { id: 'silky_terrier', label: 'Silky Terrier' },
    { id: 'skye_terrier', label: 'Skye Terrier' },
    { id: 'sloughi', label: 'Sloughi' },
    { id: 'smooth_fox_terrier', label: 'Smooth Fox Terrier' },
    { id: 'soft_coated_wheaten_terrier', label: 'Soft Coated Wheaten Terrier' },
    { id: 'spanish_water_dog', label: 'Spanish Water Dog' },
    { id: 'spinone_italiano', label: 'Spinone Italiano' },
    { id: 'staffordshire_bull_terrier', label: 'Staffordshire Bull Terrier' },
    { id: 'standard_schnauzer', label: 'Standard Schnauzer' },
    { id: 'sussex_spaniel', label: 'Sussex Spaniel' },
    { id: 'swedish_vallhund', label: 'Swedish Vallhund' },
    { id: 'tibetan_mastiff', label: 'Tibetan Mastiff' },
    { id: 'tibetan_spaniel', label: 'Tibetan Spaniel' },
    { id: 'tibetan_terrier', label: 'Tibetan Terrier' },
    { id: 'toy_fox_terrier', label: 'Toy Fox Terrier' },
    { id: 'treeing_walker_coonhound', label: 'Treeing Walker Coonhound' },
    { id: 'vizsla', label: 'Vizsla' },
    { id: 'weimaraner', label: 'Weimaraner' },
    { id: 'welsh_springer_spaniel', label: 'Welsh Springer Spaniel' },
    { id: 'welsh_terrier', label: 'Welsh Terrier' },
    { id: 'west_highland_white_terrier', label: 'West Highland White Terrier' },
    { id: 'whippet', label: 'Whippet' },
    { id: 'wire_fox_terrier', label: 'Wire Fox Terrier' },
    { id: 'wirehaired_pointing_griffon', label: 'Wirehaired Pointing Griffon' },
    { id: 'wirehaired_vizsla', label: 'Wirehaired Vizsla' },
    { id: 'xoloitzcuintli', label: 'Xoloitzcuintli' },
    { id: 'yorkshire_terrier', label: 'Yorkshire Terrier' },
]

export const DOG_BREED_IDS = new Set(DOG_BREEDS.map((row) => row.id))

export const POPULAR_DOG_BREED_IDS = [
    'mixed_unknown',
    'french_bulldog',
    'labrador_retriever',
    'golden_retriever',
    'german_shepherd_dog',
    'dachshund',
    'poodle',
    'beagle',
    'rottweiler',
    'german_shorthaired_pointer',
    'bulldog',
    'cane_corso',
    'cavalier_king_charles_spaniel',
    'yorkshire_terrier',
    'australian_shepherd',
    'doberman_pinscher',
    'pembroke_welsh_corgi',
    'miniature_schnauzer',
    'boxer',
    'pomeranian',
    'bernese_mountain_dog',
] as const

export const DEFAULT_ALLOWED_ANIMAL_TYPES = ['dog', 'cat']

export function defaultCountsTowardCap(): Record<string, boolean> {
    return { ...DEFAULT_COUNTS_TOWARD_CAP }
}

export function emptyPetsPolicy(): PetsPolicyDraft {
    return {
        allowed_animal_types: [...DEFAULT_ALLOWED_ANIMAL_TYPES],
        dog_breeds: { mode: 'all', ids: [] },
        max_pets_per_unit: '',
        counts_toward_cap: defaultCountsTowardCap(),
        deposit: '',
        fee: '',
        rent: '',
    }
}

export function clonePetsPolicy(pets: PetsPolicyDraft): PetsPolicyDraft {
    return {
        allowed_animal_types: [...pets.allowed_animal_types],
        dog_breeds: {
            mode: pets.dog_breeds.mode,
            ids: [...pets.dog_breeds.ids],
        },
        max_pets_per_unit: pets.max_pets_per_unit ?? '',
        counts_toward_cap: {
            ...defaultCountsTowardCap(),
            ...(pets.counts_toward_cap ?? {}),
        },
        deposit: pets.deposit ?? '',
        fee: pets.fee ?? '',
        rent: pets.rent ?? '',
    }
}

function uniqueKnownIds(raw: unknown, allowed: Set<string>): string[] {
    if (!Array.isArray(raw)) return []
    const seen = new Set<string>()
    const next: string[] = []
    for (const item of raw) {
        const value = String(item ?? '').trim()
        if (!allowed.has(value) || seen.has(value)) continue
        seen.add(value)
        next.push(value)
    }
    return next
}

export function petsPolicyFromPack(raw: unknown): PetsPolicyDraft {
    const base = emptyPetsPolicy()
    if (!raw || typeof raw !== 'object') return base
    const payload = raw as Record<string, unknown>
    const types = uniqueKnownIds(
        payload.allowed_animal_types,
        new Set(ANIMAL_TYPES.map((row) => row.id)),
    )
    const breedsIn =
        payload.dog_breeds && typeof payload.dog_breeds === 'object'
            ? (payload.dog_breeds as Record<string, unknown>)
            : null
    let mode: DogBreedMode = 'all'
    let ids: string[] = []
    if (breedsIn) {
        if (breedsIn.mode === 'allowlist' || breedsIn.mode === 'all') {
            mode = breedsIn.mode
        }
        ids = uniqueKnownIds(breedsIn.ids, DOG_BREED_IDS)
    } else if (Array.isArray(payload.allowed_dog_breeds)) {
        mode = 'allowlist'
        ids = uniqueKnownIds(payload.allowed_dog_breeds, DOG_BREED_IDS)
    }
    const countsIn =
        payload.counts_toward_cap &&
        typeof payload.counts_toward_cap === 'object'
            ? (payload.counts_toward_cap as Record<string, unknown>)
            : {}
    const countsTowardCap = defaultCountsTowardCap()
    for (const row of ANIMAL_TYPES) {
        if (Object.prototype.hasOwnProperty.call(countsIn, row.id)) {
            countsTowardCap[row.id] = coerceBool(
                countsIn[row.id],
                countsTowardCap[row.id],
            )
        }
    }
    return {
        allowed_animal_types:
            payload.allowed_animal_types == null && types.length === 0
                ? base.allowed_animal_types
                : types,
        dog_breeds: {
            mode,
            ids: mode === 'all' ? [] : ids,
        },
        max_pets_per_unit: stringifyFee(payload.max_pets_per_unit),
        counts_toward_cap: countsTowardCap,
        deposit: stringifyFee(payload.deposit),
        fee: stringifyFee(payload.fee),
        rent: stringifyFee(payload.rent),
    }
}

function coerceBool(value: unknown, fallback: boolean): boolean {
    if (typeof value === 'boolean') return value
    const text = String(value ?? '')
        .trim()
        .toLowerCase()
    if (['1', 'true', 'yes', 'on'].includes(text)) return true
    if (['0', 'false', 'no', 'off'].includes(text)) return false
    return fallback
}

function stringifyFee(value: unknown): string {
    if (value == null) return ''
    return String(value).trim()
}

export function allDogBreedIds(): string[] {
    return DOG_BREEDS.map((row) => row.id)
}

export function allowedAnimalTypeOptions(policy: PetsPolicyDraft): PetOption[] {
    const allowed = new Set(policy.allowed_animal_types)
    return ANIMAL_TYPES.filter((row) => allowed.has(row.id))
}

export function allowedDogBreedOptions(policy: PetsPolicyDraft): PetOption[] {
    if (!policy.allowed_animal_types.includes(DOG_ANIMAL_TYPE_ID)) {
        return []
    }
    if (policy.dog_breeds.mode === 'all') {
        return [...DOG_BREEDS]
    }
    const allowed = new Set(policy.dog_breeds.ids)
    return DOG_BREEDS.filter((row) => allowed.has(row.id))
}
